import React, { useState, useEffect, useRef } from 'react';
import ParticleBackground from '../components/ParticleBackground';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, Palette, Monitor, FileText, Box, Play, PenTool,
  Layers, Sparkles, Type, Brush, Scissors, Aperture, Lightbulb, Gem,
  Layout, Star, Hexagon, Triangle, ChevronLeft, ChevronRight
} from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import { services, featuredCaseStudies, testimonials, blogPosts, studioStats } from '../data/mockData';

const iconMap = {
  Palette, Monitor, FileText, Box, Play, PenTool, Layers, Sparkles, Type,
  Brush, Scissors, Aperture, Lightbulb, Gem, Layout, Star, Hexagon, Triangle
};

const TypewriterText = ({ text }) => {
  const words = text.split(" ");
  return (
    <span className="inline-block">
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 5 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.3,
            delay: i * 0.02,
            ease: "easeOut"
          }}
          className="inline-block mr-1"
        >
          {word}
        </motion.span>
      ))}
    </span>
  );
};

/* ---- Typewriter Effect Components ---- */
const Character = ({ children, index }) => {
  return (
    <motion.span
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.3,
        delay: index * 0.03, // specific staggered delay per character
        ease: "easeOut"
      }}
      className="inline-block"
    >
      {children}
    </motion.span>
  );
};

const TypewriterTitle = ({ text }) => {
  // Split text into words to handle wrapping gracefully, then characters
  const words = text.split(" ");
  let charIndex = 0; // Global index for continuous staggering

  return (
    <h1
      className="text-5xl md:text-7xl lg:text-[82px] leading-[1.05] tracking-tight text-[#1a1a1a] mb-10"
      style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
    >
      {words.map((word, wIndex) => (
        <span key={wIndex} className="inline-block mr-[0.25em] whitespace-nowrap">
          {word.split("").map((char, cIndex) => {
            const currentDelay = charIndex;
            charIndex++;
            return (
              <Character key={cIndex} index={currentDelay}>
                {char}
              </Character>
            );
          })}
        </span>
      ))}
    </h1>
  );
};


/* ---- Floating Design Icons ---- */
const FloatingIcons = () => {
  const icons = [
    { Icon: Palette, x: '8%', y: '15%', delay: 0 },
    { Icon: PenTool, x: '85%', y: '20%', delay: 0.5 },
    { Icon: Layers, x: '15%', y: '70%', delay: 1 },
    { Icon: Type, x: '78%', y: '65%', delay: 1.5 },
    { Icon: Sparkles, x: '45%', y: '85%', delay: 0.3 },
    { Icon: Brush, x: '92%', y: '45%', delay: 0.8 },
    { Icon: Aperture, x: '5%', y: '45%', delay: 1.2 },
    { Icon: Layout, x: '55%', y: '10%', delay: 0.6 },
    { Icon: Star, x: '30%', y: '90%', delay: 1.8 },
    { Icon: Gem, x: '70%', y: '88%', delay: 0.4 },
    { Icon: Hexagon, x: '20%', y: '30%', delay: 1.1 },
    { Icon: Triangle, x: '90%', y: '80%', delay: 0.7 },
  ];

  // These icons should start appearing after the text calculates.
  // Assuming text takes roughly ~45 chars * 0.03s = 1.35s to animate.
  // We'll add a base delay of 1.5s to all icons.
  const baseDelay = 1.8;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {icons.map(({ Icon, x, y, delay }, i) => (
        <motion.div
          key={i}
          className="absolute text-[#d0d0d0]"
          style={{ left: x, top: y }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: baseDelay + delay, duration: 0.8, ease: 'backOut' }}
        >
          <motion.div
            animate={{ y: [-4, 4, -4] }}
            transition={{ duration: 3 + delay, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Icon size={20} strokeWidth={1.5} />
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
};

/* ---- Hero Section ---- */
const HeroSection = () => (
  <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 3.0, ease: "easeOut" }}
      className="absolute inset-0 z-0"
    >
      <ParticleBackground />
    </motion.div>
    <FloatingIcons />
    <div className="relative z-10 text-center max-w-4xl mx-auto px-6 pt-20">
      
      {/* "Grapix" Label - Staggers in slightly later */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
        className="mb-6"
      >
        <div className="mb-8 inline-block">
          <img 
            src="/assets/logo_full_dark.svg" 
            alt="Grapix" 
            className="h-6 w-auto opacity-90" 
          />
        </div>
      </motion.div>

      {/* Main Title - Starts effectively immediately (chars have own delay) */}
      <TypewriterTitle text="Elevate your brand with next-generation design" />

      {/* Buttons - Fade in after text is mostly done */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.8 }}
        className="flex items-center justify-center gap-4 flex-wrap"
      >
        <Link
          to="/portfolio"
          className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white px-7 py-3 rounded-full text-sm font-medium hover:bg-[#333] transition-colors duration-200"
        >
          <Sparkles size={16} />
          View Portfolio
        </Link>
        <Link
          to="/consult"
          className="inline-flex items-center gap-2 border border-[#dadce0] text-[#1a1a1a] px-7 py-3 rounded-full text-sm hover:bg-[#f8f9fa] transition-colors duration-200"
        >
          Book a Consult
        </Link>
      </motion.div>
    </div>
  </section>
);

/* ---- About Preview ---- */
const AboutPreview = () => (
  <section className="py-24 lg:py-32 bg-white relative">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <ScrollReveal>
        <div className="max-w-4xl">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-5 h-5 rounded bg-[#1a1a1a] flex items-center justify-center">
              <span className="text-white text-[10px] font-bold" style={{ fontFamily: "'Instrument Serif', serif" }}>G</span>
            </div>
            <span className="text-[#5f6368] text-xs tracking-wide uppercase">About Grapix</span>
          </div>
          <p
            className="text-2xl md:text-3xl lg:text-4xl leading-relaxed text-[#1a1a1a] tracking-tight"
            style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
          >
            <TypewriterText text="Grapix is a design studio crafting memorable brand experiences. We blend strategic thinking with creative excellence to help brands find their voice and visual presence." />
          </p>
        </div>
      </ScrollReveal>
    </div>
  </section>
);

/* ---- Services Section ---- */
const ServicesSection = () => (
  <section className="py-24 lg:py-32 bg-[#f8f9fa]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <ScrollReveal>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[#5f6368] text-xs tracking-wide uppercase">What We Do</span>
        </div>
        <h2
          className="text-3xl md:text-4xl lg:text-5xl text-[#1a1a1a] mb-16 tracking-tight"
          style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
        >
          Services built for your brand
        </h2>
      </ScrollReveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, i) => {
          const Icon = iconMap[service.icon] || Palette;
          return (
            <ScrollReveal key={service.id} delay={i * 100}>
              <div className="group bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300 border border-[#f0f0f0] h-full">
                <div className="w-10 h-10 rounded-xl bg-[#f8f9fa] flex items-center justify-center mb-5 group-hover:bg-[#1a1a1a] transition-colors duration-300">
                  <Icon size={20} className="text-[#5f6368] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-lg font-medium text-[#1a1a1a] mb-2">{service.title}</h3>
                <p className="text-sm text-[#5f6368] leading-relaxed">{service.description}</p>
              </div>
            </ScrollReveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---- Case Studies Section ---- */
const CaseStudiesSection = () => {
  const [activeCase, setActiveCase] = useState(0);
  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <ScrollReveal>
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className="text-[#5f6368] text-xs tracking-wide uppercase">Featured Work</span>
              <h2
                className="text-3xl md:text-4xl lg:text-5xl text-[#1a1a1a] mt-3 tracking-tight"
                style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
              >
                Built for brands<br />in the design-first era
              </h2>
            </div>
          </div>
          <p className="text-base text-[#5f6368] max-w-2xl mb-16 leading-relaxed">
            Grapix is built for creative excellence, whether you're a startup crafting your first impression,
            a heritage brand evolving for modern audiences, or anyone in between.
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {featuredCaseStudies.map((cs, i) => (
            <ScrollReveal key={cs.id} delay={i * 150}>
              <div
                className={`group cursor-pointer rounded-2xl overflow-hidden border transition-all duration-300 ${
                  activeCase === i ? 'border-[#1a1a1a] shadow-lg' : 'border-[#e8eaed] hover:border-[#ccc]'
                }`}
                onMouseEnter={() => setActiveCase(i)}
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={cs.thumbnail}
                    alt={cs.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2">
                    <div className="w-5 h-5 rounded bg-white/90 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-[#1a1a1a]" style={{ fontFamily: "'Instrument Serif', serif" }}>G</span>
                    </div>
                    <span className="text-white text-sm font-medium">{cs.title}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-medium text-[#1a1a1a] mb-1">{cs.title}</h3>
                  <p className="text-sm text-[#5f6368] leading-relaxed mb-4">{cs.description}</p>
                  <Link to={cs.link} className="inline-flex items-center gap-1 text-sm text-[#1a1a1a] font-medium hover:gap-2 transition-all">
                    View case <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---- Stats Section ---- */
const StatsSection = () => (
  <section className="py-20 bg-[#f8f9fa] border-y border-[#e8eaed]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {studioStats.map((stat, i) => (
          <ScrollReveal key={stat.label} delay={i * 100}>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-medium text-[#1a1a1a] mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                {stat.value}
              </p>
              <p className="text-sm text-[#5f6368]">{stat.label}</p>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---- Testimonials ---- */
const TestimonialsSection = () => {
  const [current, setCurrent] = useState(0);
  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <ScrollReveal>
          <span className="text-[#5f6368] text-xs tracking-wide uppercase">Client Stories</span>
          <div className="mt-10 max-w-3xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <blockquote
                  className="text-2xl md:text-3xl lg:text-4xl text-[#1a1a1a] leading-relaxed mb-8 tracking-tight"
                  style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
                >
                  "{testimonials[current].quote}"
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center text-white text-xs font-medium">
                    {testimonials[current].avatar}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1a1a1a]">{testimonials[current].author}</p>
                    <p className="text-xs text-[#5f6368]">{testimonials[current].role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="flex items-center gap-3 mt-10">
              <button
                onClick={() => setCurrent((p) => (p === 0 ? testimonials.length - 1 : p - 1))}
                className="w-10 h-10 rounded-full border border-[#dadce0] flex items-center justify-center hover:bg-[#f8f9fa] transition-colors"
              >
                <ChevronLeft size={18} className="text-[#1a1a1a]" />
              </button>
              <button
                onClick={() => setCurrent((p) => (p === testimonials.length - 1 ? 0 : p + 1))}
                className="w-10 h-10 rounded-full border border-[#dadce0] flex items-center justify-center hover:bg-[#f8f9fa] transition-colors"
              >
                <ChevronRight size={18} className="text-[#1a1a1a]" />
              </button>
              <span className="text-xs text-[#5f6368] ml-2">{current + 1} / {testimonials.length}</span>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

/* ---- Blog Preview ---- */
const BlogPreview = () => (
  <section className="py-24 lg:py-32 bg-[#f8f9fa]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <ScrollReveal>
        <div className="flex items-center justify-between mb-12">
          <div>
            <span className="text-[#5f6368] text-xs tracking-wide uppercase">Latest Insights</span>
            <h2
              className="text-3xl md:text-4xl text-[#1a1a1a] mt-3 tracking-tight"
              style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
            >
              From the Blog
            </h2>
          </div>
          <Link to="/blog" className="text-sm text-[#5f6368] hover:text-[#1a1a1a] transition-colors flex items-center gap-1">
            View all <ArrowRight size={14} />
          </Link>
        </div>
      </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogPosts.map((post, i) => (
            <ScrollReveal key={post.id} delay={i * 100}>
              <Link to={`/blog/${post.id}`} className="group block">
                <div className="rounded-2xl overflow-hidden border border-[#e8eaed] bg-white hover:shadow-lg transition-shadow duration-300">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs text-[#5f6368]">{post.date}</span>
                      <span className="text-xs text-[#5f6368]">·</span>
                      <span className="text-xs text-[#5f6368]">{post.category}</span>
                    </div>
                    <h3 className="font-medium text-[#1a1a1a] mb-2 group-hover:text-[#5f6368] transition-colors">{post.title}</h3>
                    <p className="text-sm text-[#5f6368] leading-relaxed">{post.excerpt}</p>
                  </div>
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
    </div>
  </section>
);

/* ---- Home Page ---- */
const Home = () => (
  <main>
    <HeroSection />
    <AboutPreview />
    <ServicesSection />
    <CaseStudiesSection />
    <StatsSection />
    <TestimonialsSection />
    <BlogPreview />
  </main>
);

export default Home;
